import { useEffect, useMemo, useState, type FormEvent } from "react";

import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/ui/Feedback";
import { ExcelExportButton } from "../../components/ui/ExcelExportButton";
import { SortableTh } from "../../components/ui/SortableTh";
import { exportRowsToExcel, fetchAllPages } from "../../lib/excel-export";
import { useTableSort } from "../../lib/table-sort";
import { useSettingsLookup } from "../settings/settings.queries";
import { fetchStoreReport, type StoreLookup } from "./store.api";
import { useInventoryModule } from "./inventory-module";
import { useStoreLookup, useStoreReport } from "./store.queries";

const humanize = (key: string) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

export type StoreReportKind =
  | "general-stock"
  | "department-stock"
  | "requisition"
  | "purchase-order"
  | "purchase"
  | "issue"
  | "item-stock"
  | "upcoming-expiry";

/** Column keys the backend report endpoints accept as sortBy, per report type. */
const REPORT_SORTABLE_COLUMNS: Record<StoreReportKind, readonly string[]> = {
  "general-stock": [
    "itemCode",
    "itemName",
    "receivedQuantity",
    "availableQuantity",
    "returnQuantity",
    "amount",
    "status",
    "reorderLevel",
  ],
  "department-stock": [
    "issueNumber",
    "issueDate",
    "department",
    "itemName",
    "batchNumber",
    "expiryDate",
    "issueQuantity",
    "returnQuantity",
    "netQuantity",
    "rate",
    "amount",
  ],
  issue: [
    "issueNumber",
    "issueDate",
    "department",
    "itemName",
    "batchNumber",
    "expiryDate",
    "issueQuantity",
    "returnQuantity",
    "netQuantity",
    "rate",
    "amount",
  ],
  requisition: [
    "requisitionNumber",
    "requisitionDate",
    "department",
    "status",
    "itemName",
    "requestedQuantity",
    "issuedQuantity",
    "rejectedQuantity",
    "pendingQuantity",
  ],
  "purchase-order": [
    "purchaseOrderNumber",
    "orderDate",
    "vendor",
    "status",
    "itemName",
    "quantity",
    "rate",
    "taxPercent",
    "amount",
  ],
  purchase: [
    "purchaseNumber",
    "purchaseDate",
    "invoiceNumber",
    "vendor",
    "itemName",
    "batchNumber",
    "expiryDate",
    "quantity",
    "rate",
    "amount",
  ],
  "item-stock": [
    "item",
    "vendor",
    "store",
    "batchNumber",
    "expiryDate",
    "receivedQuantity",
    "availableQuantity",
    "rate",
    "mrp",
    "taxPercent",
  ],
  "upcoming-expiry": [
    "item",
    "vendor",
    "store",
    "batchNumber",
    "expiryDate",
    "receivedQuantity",
    "availableQuantity",
    "rate",
    "mrp",
    "taxPercent",
    "daysToExpiry",
  ],
};

const reportMeta: Record<
  StoreReportKind,
  { title: string; description: string }
> = {
  "general-stock": {
    title: "General Stock Reports",
    description: "Current quantity, value, and reorder status by item.",
  },
  "department-stock": {
    title: "Department Stock Reports",
    description: "Issued and returned stock by department.",
  },
  requisition: {
    title: "Requisition Report",
    description: "Requested, issued, rejected, and pending item quantities.",
  },
  "purchase-order": {
    title: "Purchase Order Report",
    description: "Purchase order lines and their approval status.",
  },
  purchase: {
    title: "Purchase Report",
    description: "Received purchase lines, invoices, lots, and rates.",
  },
  issue: {
    title: "Item Issue Reports",
    description: "Department issues, returns, and net issued value.",
  },
  "item-stock": {
    title: "Item Stock Report",
    description:
      "Lot-level inventory, supplier, expiry, and available quantity.",
  },
  "upcoming-expiry": {
    title: "Upcoming Expiry Items",
    description: "Available lots due to expire in the selected number of days.",
  },
};
const today = () => new Date().toISOString().slice(0, 10);
const requisitionDateFormatter = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});
const formatRequisitionDate = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  const parts = Object.fromEntries(
    requisitionDateFormatter
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.day}-${parts.month}-${parts.year}`;
};
const formatExpiryDate = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.day}-${parts.month}-${parts.year}`;
};
const expiryStatus = (days: number): { label: string; tone: string } =>
  days <= 0
    ? { label: "Expired", tone: "critical" }
    : days <= 30
      ? { label: "Expire Within 1 Month", tone: "warning" }
      : { label: "Expire More Than 1 Month", tone: "active" };
const asText = (value: unknown, column?: string): string => {
  if (value === null || value === undefined) return "-";
  if (
    column === "requisitionDate" ||
    column === "issueDate" ||
    column === "orderDate" ||
    column === "purchaseDate"
  )
    return formatRequisitionDate(value);
  if (column === "expiryDate") return formatExpiryDate(value);
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const scalar = [record.name, record.vendorName, record.id].find(
      (entry) => typeof entry === "string" || typeof entry === "number",
    );
    return scalar !== undefined ? String(scalar) : "-";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  return "-";
};

export const StoreReportPage = ({ report }: { report: StoreReportKind }) => {
  const inventoryModule = useInventoryModule();
  const isPharmacy = inventoryModule === "pharmacy";
  const meta = reportMeta[report];
  const [draft, setDraft] = useState({
    fromDate: "",
    toDate: "",
    status: "",
    days: "30",
    department: "",
    vendor: "",
    store: "",
    item: "",
  });
  const [filters, setFilters] = useState(draft);
  const [expirySearch, setExpirySearch] = useState("");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const query = useStoreReport(
    report,
    sortBy
      ? {
          ...filters,
          ...(report === "upcoming-expiry" ? { days: "60" } : {}),
          sortBy,
          sortOrder,
        }
      : { ...filters, ...(report === "upcoming-expiry" ? { days: "60" } : {}) },
  );
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });
  const storeDepartments = useStoreLookup(
    "masters",
    { kind: "department" },
    !isPharmacy,
  );
  const settingsDepartments = useSettingsLookup("departments", isPharmacy);
  const departments: StoreLookup[] | undefined = isPharmacy
    ? settingsDepartments.data?.map((department) => ({
        id: department.id,
        label: String(
          department.label ?? department.departmentName ?? department.id,
        ),
      }))
    : storeDepartments.data;
  const vendors = useStoreLookup("vendors");
  const stores = useStoreLookup("masters", { kind: "store" });
  const items = useStoreLookup("items");
  const columns = useMemo(
    () =>
      Object.keys(query.data?.data[0] ?? {}).filter(
        (key) => !["id", "_id"].includes(key),
      ),
    [query.data],
  );
  const sortableColumns = REPORT_SORTABLE_COLUMNS[report];
  const expiryRows =
    report === "upcoming-expiry"
      ? (query.data?.data ?? []).filter((row) =>
          asText(row.item)
            .toLowerCase()
            .includes(expirySearch.trim().toLowerCase()),
        )
      : [];
  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
  };
  const reset = () => {
    const next = {
      fromDate: "",
      toDate: "",
      status: "",
      days: "30",
      department: "",
      vendor: "",
      store: "",
      item: "",
    };
    setDraft(next);
    setFilters(next);
    setExpirySearch("");
    setSortBy(null);
    setSortOrder("asc");
  };
  useEffect(() => {
    setSortBy(null);
    setSortOrder("asc");
  }, [report]);
  const exportToExcel = async () => {
    const rows = await fetchAllPages<Record<string, unknown>>(
      (page, limit) =>
        fetchStoreReport(report, {
          ...(sortBy ? { ...filters, sortBy, sortOrder } : filters),
          page,
          limit,
        }, inventoryModule),
      500,
    );
    const exportColumns = Object.keys(
      rows[0] ?? query.data?.data[0] ?? {},
    ).filter((key) => !["id", "_id"].includes(key));
    await exportRowsToExcel({
      fileName: `${meta.title.replace(/\s+/g, "-")}-${today()}`,
      title: meta.title,
      subtitle: meta.description,
      columns: exportColumns.map((column) => ({
        header: humanize(column),
        width: 16,
        map: (row: Record<string, unknown>) => asText(row[column], column),
      })),
      rows,
    });
  };
  if (report === "upcoming-expiry")
    return (
      <div className="page-stack upcoming-expiry-page">
        <div className="page-heading">
          <div>
            <p className="eyebrow">Store</p>
            <h1>Upcoming Expiry Items</h1>
          </div>
          <ExcelExportButton
            onExport={exportToExcel}
            disabled={!query.data || query.data.data.length === 0}
          />
        </div>
        <section className="content-card">
          <form
            className="expiry-search-bar"
            onSubmit={(event) => event.preventDefault()}
          >
            <input
              aria-label="Item Name"
              placeholder="Item Name"
              value={expirySearch}
              onChange={(event) => setExpirySearch(event.target.value)}
            />
            <button className="button button-primary" type="submit">
              Search
            </button>
          </form>
          <h2 className="expiry-list-title">
            Upcoming items will expire within the next two months
          </h2>
          {query.isLoading ? <LoadingState /> : null}
          {query.isError ? (
            <ErrorState
              title="Unable to load report"
              message="Please try again."
              action={{
                label: "Try again",
                onClick: () => void query.refetch(),
              }}
            />
          ) : null}
          {query.data && expiryRows.length === 0 ? (
            <EmptyState
              title="No records found"
              message="Adjust the search and try again."
            />
          ) : null}
          {expiryRows.length > 0 ? (
            <div className="table-scroll">
              <table className="expiry-items-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Item Name</th>
                    <th>Batch</th>
                    <th>Expiry Date</th>
                  </tr>
                </thead>
                <tbody>
                  {expiryRows.map((row, index) => {
                    const status = expiryStatus(Number(row.daysToExpiry));
                    return (
                      <tr key={String(row.id)}>
                        <td>{index + 1}</td>
                        <td>{asText(row.item)}</td>
                        <td>{asText(row.batchNumber)}</td>
                        <td>
                          <div>{formatExpiryDate(row.expiryDate)}</div>
                          <span className={`status-badge ${status.tone}`}>
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    );
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Store Reports</p>
          <h1>{meta.title}</h1>
          <p>{meta.description}</p>
        </div>
        <ExcelExportButton
          onExport={exportToExcel}
          disabled={!query.data || query.data.data.length === 0}
        />
      </div>
      <section className="content-card">
        <form className="store-report-filters" onSubmit={apply}>
          <label>
            From date
            <input
              type="date"
              value={draft.fromDate}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  fromDate: event.target.value,
                }))
              }
            />
          </label>
          <label>
            To date
            <input
              type="date"
              value={draft.toDate}
              max={today()}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  toDate: event.target.value,
                }))
              }
            />
          </label>
          {["department-stock", "requisition", "issue"].includes(report) ? (
            <FilterSelect
              label="Department"
              value={draft.department}
              options={departments}
              onChange={(department) =>
                setDraft((current) => ({ ...current, department }))
              }
            />
          ) : null}
          {["purchase-order", "purchase", "item-stock"].includes(report) ? (
            <FilterSelect
              label="Vendor"
              value={draft.vendor}
              options={vendors.data}
              onChange={(vendor) =>
                setDraft((current) => ({ ...current, vendor }))
              }
            />
          ) : null}
          {[
            "general-stock",
            "purchase-order",
            "purchase",
            "item-stock",
            "upcoming-expiry",
          ].includes(report) ? (
            <FilterSelect
              label="Store room"
              value={draft.store}
              options={stores.data}
              onChange={(store) =>
                setDraft((current) => ({ ...current, store }))
              }
            />
          ) : null}
          {report !== "general-stock" ? (
            <FilterSelect
              label="Item"
              value={draft.item}
              options={items.data}
              onChange={(item) => setDraft((current) => ({ ...current, item }))}
            />
          ) : null}
          {report === "general-stock" ? (
            <label>
              Stock status
              <select
                value={draft.status}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                <option value="">All</option>
                <option>Available</option>
                <option>Low Stock</option>
                <option>Out Of Stock</option>
              </select>
            </label>
          ) : null}
          <div>
            <button className="button button-primary" type="submit">
              Filter
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={reset}
            >
              Reset
            </button>
          </div>
        </form>
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? (
          <ErrorState
            title="Unable to load report"
            message="Please try again."
            action={{ label: "Try again", onClick: () => void query.refetch() }}
          />
        ) : null}
        {query.data?.summary ? (
          <div className="quick-stat-row">
            {Object.entries(query.data.summary).map(([key, value]) => (
              <div key={key}>
                <strong>{Number(value).toLocaleString()}</strong>
                <small>{key.replace(/([A-Z])/g, " $1")}</small>
              </div>
            ))}
          </div>
        ) : null}
        {query.data && query.data.data.length === 0 ? (
          <EmptyState
            title="No records found"
            message="Adjust the filters and try again."
          />
        ) : null}
        {query.data && query.data.data.length > 0 ? (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  {columns.map((column) =>
                    sortableColumns.includes(column) ? (
                      <SortableTh
                        key={column}
                        control={tableSort}
                        column={column}
                      >
                        {humanize(column)}
                      </SortableTh>
                    ) : (
                      <th key={column}>{humanize(column)}</th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((row) => (
                  <tr key={String(row.id)}>
                    {columns.map((column) => (
                      <td key={column}>{asText(row[column], column)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[] | undefined;
  onChange: (value: string) => void;
}) => (
  <label>
    {label}
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">All {label}s</option>
      {options?.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);
